import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  CreateModelPayload,
  CreateModelResponse,
  ModelDetailRecord,
  ModelParamDefinition,
  PklValidationError,
  PklValidationResult,
  UpdateModelPayload,
} from '../types/modelParam'
import type { ModelListParams, ModelListResponse } from '../types'

function buildListQueryParams(filters: ModelListParams): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: filters.page,
    perPage: filters.perPage,
    orderBy: filters.orderBy,
    sortBy: filters.sortBy,
  }

  const search = String(filters.search ?? '').trim()
  if (search) {
    params.search = search
  }

  const status = filters.is_active
  if (status === '0' || status === '1') {
    params.is_active = status
  }

  return params
}

/** Angular `ModelListComponent.loadModels` → GET `/api/models/`. */
export async function getModelList(filters: ModelListParams): Promise<ModelListResponse> {
  try {
    const { data } = await apiClient.get<ModelListResponse>(API_ENDPOINTS.MODEL_LIST, {
      params: buildListQueryParams(filters),
    })
    return {
      data: Array.isArray(data?.data) ? data.data : [],
      current: Number(data?.current ?? 1),
      last: Number(data?.last ?? 1),
      total: Number(data?.total ?? 0),
    }
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Error loading models. Please try again.')
  }
}

function extractCanDeleteMessage(data: unknown): string | null {
  if (typeof data === 'string' && data.trim()) {
    return data.trim()
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    const msg = o.message ?? o.msg ?? o.Message
    if (msg != null && String(msg).trim()) {
      return String(msg).trim()
    }
  }
  return null
}

/** Angular `deleteModelConfirm` → GET `/api/models/can_delete_model/:id`. */
export async function fetchCanDeleteModel(
  modelId: string | number,
): Promise<{ allowed: boolean; message: string }> {
  try {
    const { data } = await apiClient.get<unknown>(API_ENDPOINTS.MODEL_CAN_DELETE(modelId))
    if (!data) {
      return {
        allowed: false,
        message: 'Cannot delete this model as it is assigned to properties.',
      }
    }
    return {
      allowed: true,
      message: extractCanDeleteMessage(data) || 'Are you sure you want to delete this model?',
    }
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Error checking model deletion')
  }
}

/** Angular `deleteModel` → DELETE `/api/models/:id`. */
export async function deleteModel(modelId: string | number): Promise<void> {
  try {
    await apiClient.delete(API_ENDPOINTS.MODEL_DETAIL(modelId))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete model')
  }
}

function extractModelParamDefinitions(data: unknown): ModelParamDefinition[] {
  if (Array.isArray(data)) {
    return data as ModelParamDefinition[]
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    const definitions = o.definitions ?? (o.data as Record<string, unknown> | undefined)?.definitions
    if (Array.isArray(definitions)) {
      return definitions as ModelParamDefinition[]
    }
  }
  return []
}

/** Angular `ModelParamDefinitionService.getDefinitions` → GET `/api/models/param-definitions`. */
export async function getModelParamDefinitions(): Promise<ModelParamDefinition[]> {
  try {
    const { data } = await apiClient.get<unknown>(API_ENDPOINTS.MODEL_PARAM_DEFINITIONS)
    return extractModelParamDefinitions(data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load model calculation variables')
  }
}

function extractCreatedModelId(data: CreateModelResponse | null | undefined): number | null {
  if (!data) return null
  const direct = Number(data.model_id)
  if (Number.isFinite(direct) && direct > 0) return direct
  const nested = Number(data.data?.model_id)
  if (Number.isFinite(nested) && nested > 0) return nested
  return null
}

/** Angular `ModelAddComponent.createModel` → POST `/api/models` (multipart). */
export async function createModel(payload: CreateModelPayload): Promise<number> {
  try {
    const formData = new FormData()
    formData.append('model_name', payload.model_name)
    formData.append('description', payload.description ?? '')
    formData.append('is_active', String(payload.is_active))
    formData.append('params', payload.params)
    formData.append('model_processor_file', payload.model_processor_file)
    if (payload.model_pkl_file) {
      formData.append('model_pkl_file', payload.model_pkl_file)
    }

    const { data } = await apiClient.post<CreateModelResponse>(API_ENDPOINTS.MODEL_CREATE, formData)
    const modelId = extractCreatedModelId(data)
    if (!modelId) {
      throw new Error('Failed to create model')
    }
    return modelId
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to create model')
  }
}

function extractModelDetail(data: unknown): ModelDetailRecord {
  if (data && typeof data === 'object' && 'model_id' in data) {
    return data as ModelDetailRecord
  }
  if (data && typeof data === 'object' && 'data' in data) {
    const nested = (data as { data?: ModelDetailRecord }).data
    if (nested && typeof nested === 'object') {
      return nested
    }
  }
  throw new Error('Failed to load model details')
}

/** Angular `ModelEditComponent.getModelDetails` → GET `/api/models/:id`. */
export async function getModelDetail(modelId: string | number): Promise<ModelDetailRecord> {
  try {
    const { data } = await apiClient.get<unknown>(API_ENDPOINTS.MODEL_DETAIL(modelId))
    return extractModelDetail(data)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load model details')
  }
}

function buildUpdateFormData(payload: UpdateModelPayload): FormData {
  const formData = new FormData()
  formData.append('model_name', payload.model_name)
  formData.append('description', payload.description ?? '')
  formData.append('is_active', String(payload.is_active))
  formData.append('params', payload.params)
  if (payload.model_processor_file) {
    formData.append('model_processor_file', payload.model_processor_file)
  }
  if (payload.model_pkl_file) {
    formData.append('model_pkl_file', payload.model_pkl_file)
  }
  return formData
}

/** Angular `ModelEditComponent.updateModel` → POST `/api/models/:id` (multipart when files). */
export async function updateModel(
  modelId: string | number,
  payload: UpdateModelPayload,
): Promise<void> {
  try {
    const hasUpload = !!(payload.model_processor_file || payload.model_pkl_file)
    const body = hasUpload ? buildUpdateFormData(payload) : {
      model_name: payload.model_name,
      description: payload.description ?? '',
      is_active: payload.is_active,
      params: payload.params,
    }

    await apiClient.post(API_ENDPOINTS.MODEL_DETAIL(modelId), body)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update model')
  }
}

/** Angular `ModelTestPklDialogComponent.submitForm` → POST `/api/models/:id/validate-pkl`. */
export async function validateModelPkl(
  modelId: string | number,
  params: Record<string, unknown>,
): Promise<PklValidationResult> {
  try {
    const { data } = await apiClient.post<PklValidationResult>(
      API_ENDPOINTS.MODEL_VALIDATE_PKL(modelId),
      { params },
    )
    return data ?? {}
  } catch (error) {
    const message = extractErrorMessage(error)
    const err = error as { response?: { data?: PklValidationError } }
    const payload = err.response?.data
    if (payload?.data) {
      throw Object.assign(new Error(message || 'Failed to test model files.'), { data: payload.data })
    }
    if (payload?.tests) {
      throw Object.assign(new Error(message || 'Failed to test model files.'), { data: payload })
    }
    throw new Error(message || 'Failed to test model files.')
  }
}
